import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

const ConsultantTermsDialog = ({ open, onClose, onAgree }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>Terms & Conditions for Loan Consultants</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" paragraph>
          <strong>Effective Date:</strong> July 25, 2025
        </Typography>

        <Typography variant="body2" paragraph>
          Welcome to Dequitec.ai ("Platform," "we," "us," or "our"). These Terms
          & Conditions ("Terms") govern your access to and use of our platform
          as a Loan Consultant. By registering for an account and using our
          services, you ("Consultant," "you," or "your") agree to be bound by
          these Terms, our Privacy Policy, and the specific Consultant Agreement
          you enter into with us. If you do not agree to these Terms, you may
          not use our services.
        </Typography>

        <Typography variant="h6">1. Introduction</Typography>
        <Typography variant="body2" paragraph>
          Dequitec.ai operates as a loan aggregation platform that connects
          borrowers seeking financial assistance with various lenders and
          facilitates this connection through the services of independent loan
          consultants. These Terms outline your rights and obligations as a
          Consultant utilizing our Platform.
        </Typography>

        <Typography variant="h6">2. Acceptance of Terms</Typography>
        <Typography variant="body2" paragraph>
          By clicking "I agree," "Register," or by accessing or using any part
          of the Platform, you acknowledge that you have read, understood, and
          agree to be bound by these Terms, which form a legally binding
          agreement between you and Dequitec.ai.
        </Typography>

        <Typography variant="h6">3. Eligibility</Typography>
        <Typography variant="body2" paragraph>
          To register and operate as a Consultant on our Platform, you must:
        </Typography>
        <ul>
          <li>
            Be at least 18 years old and have the legal capacity to enter into
            contracts.
          </li>
          <li>
            Possess all necessary licenses, registrations, and certifications
            required by law to provide loan consultancy services in your
            operating jurisdiction.
          </li>
          <li>
            Comply with all applicable laws, regulations, and guidelines related
            to loan consultancy and financial services.
          </li>
          <li>
            Provide accurate, complete, and truthful information during the
            registration process and throughout your engagement with the
            Platform.
          </li>
        </ul>

        <Typography variant="h6">4. Registration and Account</Typography>
        <Typography variant="body2" paragraph>
          <strong>4.1. Account Creation:</strong> To become a Consultant, you
          must complete the registration form provided on our Platform,
          submitting all required information.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>4.2. Account Security:</strong> You are solely responsible for
          maintaining the confidentiality of your account login credentials and
          for all activities that occur under your account. You agree to notify
          us immediately of any unauthorized use of your account or any other
          breach of security. We will not be liable for any loss or damage
          arising from your failure to comply with this security obligation.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>4.3. Accuracy of Information:</strong> You agree to provide
          accurate, current, and complete information during registration and to
          promptly update your account information to keep it accurate, current,
          and complete.
        </Typography>

        <Typography variant="h6">5. Role of the Platform</Typography>
        <Typography variant="body2" paragraph>
          <strong>5.1. Facilitation:</strong> Dequitec.ai acts solely as an
          intermediary platform to connect Consultants with potential borrowers
          and lenders. We do not provide loan consultancy services ourselves,
          nor do we act as a lender or financial advisor.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>5.2. No Guarantee:</strong> We do not guarantee a specific
          volume of leads, successful loan closures, or any particular income
          level for Consultants. The success of a loan application depends on
          various factors, including the borrower's eligibility, the lender's
          criteria, and your consultancy efforts.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>5.3. Information Exchange:</strong> We facilitate the secure
          exchange of relevant information between borrowers, consultants, and
          lenders, subject to our Privacy Policy and applicable data protection
          laws.
        </Typography>

        <Typography variant="h6">6. Consultant Responsibilities</Typography>
        <Typography variant="body2" paragraph>
          <strong>6.1. Professional Conduct:</strong> You agree to conduct
          yourself with the highest degree of professionalism, integrity, and
          ethical standards in all interactions with borrowers, lenders, and
          Dequitec.ai.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>6.2. Compliance:</strong> You are solely responsible for
          ensuring that your loan consultancy services comply with all
          applicable local, state, and national laws, regulations, and industry
          standards, including but not limited to those related to consumer
          protection, financial services, data privacy, and anti-money
          laundering (AML).
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>6.3. Due Diligence:</strong> You must exercise due diligence
          in verifying borrower information, assessing their needs, and guiding
          them through the loan application process.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>6.4. Transparency:</strong> You must be transparent with
          borrowers regarding your role, any fees or commissions you may charge
          (if applicable and separate from platform fees), and the terms and
          conditions of loan products.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>6.5. Confidentiality:</strong> You agree to maintain strict
          confidentiality of all borrower and lender information accessed
          through the Platform and to use such information solely for the
          purpose of facilitating loan applications.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>6.6. No Misrepresentation:</strong> You shall not make any
          false, misleading, or deceptive statements or representations to
          borrowers or lenders regarding loan products, terms, or your services.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>6.7. No Direct Solicitation (Outside Platform):</strong> You
          agree not to directly solicit or engage with borrowers or lenders
          introduced through the Platform for services outside the Platform's
          purview, unless explicitly permitted by the Platform.
        </Typography>

        <Typography variant="h6">7. Data Privacy</Typography>
        <Typography variant="body2" paragraph>
          Your use of the Platform and your provision of information (including
          personal data) are subject to our Privacy Policy, which is
          incorporated into these Terms by reference. You acknowledge and agree
          that you have read and understood our Privacy Policy. You also agree
          to comply with all applicable data protection laws when handling any
          personal data of borrowers or lenders.
        </Typography>

        <Typography variant="h6">8. Intellectual Property</Typography>
        <Typography variant="body2" paragraph>
          The Platform, its content, features, and functionality (including but
          not limited to all information, software, text, displays, images,
          video, and audio, and the design, selection, and arrangement thereof)
          are owned by Dequitec.ai, its licensors, or other providers of such
          material and are protected by copyright, trademark, patent, trade
          secret, and other intellectual property or proprietary rights laws.
        </Typography>

        <Typography variant="h6">
          9. Disclaimers and Limitation of Liability
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>9.1. "As Is" Basis:</strong> The Platform and all services
          provided through it are provided on an "as is" and "as available"
          basis, without any warranties of any kind, either express or implied,
          including but not limited to implied warranties of merchantability,
          fitness for a particular purpose, or non-infringement.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>9.2. No Endorsement:</strong> We do not endorse or guarantee
          the quality, reliability, or suitability of any borrower, lender, or
          loan product. Your interactions with borrowers and lenders are solely
          at your own risk.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>9.3. Limitation of Liability:</strong> To the fullest extent
          permitted by law, Dequitec.ai shall not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or any loss
          of profits or revenues, whether incurred directly or indirectly, or
          any loss of data, use, goodwill, or other intangible losses, resulting
          from (a) your access to or use of or inability to access or use the
          Platform; (b) any conduct or content of any third party on the
          Platform; (c) any content obtained from the Platform; or (d)
          unauthorized access, use, or alteration of your transmissions or
          content.
        </Typography>

        <Typography variant="h6">10. Indemnification</Typography>
        <Typography variant="body2" paragraph>
          You agree to indemnify, defend, and hold harmless Dequitec.ai, its
          affiliates, officers, directors, employees, agents, and licensors from
          and against any and all claims, liabilities, damages, losses, costs,
          expenses, or fees (including reasonable attorneys' fees) arising from
          or relating to:
        </Typography>
        <ul>
          <li>
            Your violation of these Terms or any applicable law or regulation.
          </li>
          <li>
            Your provision of consultancy services, including any acts or
            omissions.
          </li>
          <li>Your interactions with borrowers or lenders.</li>
          <li>Any misrepresentation or breach of warranty made by you.</li>
        </ul>

        <Typography variant="h6">11. Termination</Typography>
        <Typography variant="body2" paragraph>
          <strong>11.1. By You:</strong> You may terminate your account at any
          time by contacting us.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>11.2. By Us:</strong> We may suspend or terminate your account
          and access to the Platform immediately, without prior notice or
          liability, for any reason whatsoever, including without limitation if
          you breach these Terms, the Consultant Agreement, or if your conduct
          is detrimental to the Platform or its users.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>11.3. Effect of Termination:</strong> Upon termination, your
          right to use the Platform will immediately cease. All provisions of
          these Terms which by their nature should survive termination shall
          survive termination, including, without limitation, ownership
          provisions, warranty disclaimers, indemnity, and limitations of
          liability.
        </Typography>

        <Typography variant="h6">12. Governing Law and Jurisdiction</Typography>
        <Typography variant="body2" paragraph>
          These Terms shall be governed by and construed in accordance with the
          laws of India, without regard to its conflict of law principles. Any
          legal action or proceeding arising under these Terms will be brought
          exclusively in the courts located in Delhi, and you hereby consent to
          the personal jurisdiction and venue therein.
        </Typography>

        <Typography variant="h6">13. Changes to Terms</Typography>
        <Typography variant="body2" paragraph>
          We reserve the right, at our sole discretion, to modify or replace
          these Terms at any time. If a revision is material, we will provide at
          least 30 days' notice prior to any new terms taking effect. What
          constitutes a material change will be determined at our sole
          discretion. By continuing to access or use our Platform after those
          revisions become effective, you agree to be bound by the revised
          terms.
        </Typography>

        <Typography variant="h6">14. Contact Information</Typography>
        <Typography variant="body2" paragraph>
          If you have any questions about these Terms, please contact us at{" "}
          <strong>contact@dequitec.com</strong>.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Decline
        </Button>
        <Button onClick={onAgree} color="primary" variant="contained">
          I Agree
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsultantTermsDialog;
